package com.swetdot.swetgram.ui.design

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxScope
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.pulltorefresh.PullToRefreshBox
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.Dp
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens

/**
 * Empty, error and loading states.
 *
 * SDS §25's component checklist asks every component for all three, and §17 fixes the voice for
 * the first: short, helpful, calm. None of the copy is authored here -- it is passed in, because
 * "No chats yet." has to become "Пока нет чатов." without touching the design system.
 */

/** The user-facing default strings, gathered so they are easy to find when localization lands. */
// TODO(localization): these move into resources for EN + RU.
private const val DEFAULT_LOADING_LABEL = "Loading"
private const val DEFAULT_RETRY_LABEL = "Retry"
private const val DEFAULT_DISMISS_LABEL = "Dismiss"

// -------------------------------------------------------------------------------------------
// Pull to refresh
// -------------------------------------------------------------------------------------------

/**
 * Pull down to reload whatever this contains.
 *
 * It belongs in the design system rather than on the one screen that needs it today, because the
 * gesture has to mean the same thing everywhere it appears -- and because the alternative is what
 * the app had: no way at all to ask a list to reload. Every other refresh in the app is triggered
 * by something the user cannot see (a socket frame, a screen entry), so a list that looked wrong
 * left them with nothing to try.
 *
 * [onRefresh] is nullable and null means the gesture DOES NOT EXIST -- no indicator, no drag, no
 * bare `Box` pretending otherwise. A spinner that appears and resolves to nothing is worse than
 * no gesture, because it teaches the user the refresh happened.
 *
 * [refreshing] must be driven by the caller's own in-flight state. Material's box will not hide
 * the indicator on its own; it holds it until it is told the work finished, which is exactly the
 * contract that makes a pull feel honest.
 *
 * The indicator is Material's default, and deliberately so: it reads `MaterialTheme.colorScheme`,
 * which `SwetgramColors.toColorScheme` already projects the tokens onto, so it arrives in
 * `surface` and `accent.blue` without a hand-built copy to keep in step.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SwetPullToRefresh(
    refreshing: Boolean,
    onRefresh: (() -> Unit)?,
    modifier: Modifier = Modifier,
    content: @Composable BoxScope.() -> Unit,
) {
    if (onRefresh == null) {
        Box(modifier, content = content)
        return
    }
    PullToRefreshBox(
        isRefreshing = refreshing,
        onRefresh = onRefresh,
        modifier = modifier,
        content = content,
    )
}

// -------------------------------------------------------------------------------------------
// Empty state
// -------------------------------------------------------------------------------------------

/**
 * SDS §17. Two short lines and, at most, one thing to do about it.
 *
 * The illustration is a slot rather than an asset: §10 hands illustrations to the brand
 * specialist and none are bundled yet, so the design system reserves the space without
 * inventing a picture.
 *
 * The title is `headline` (20sp/600), not §5.3's `display.large` -- 36sp is for the marketing
 * and onboarding moments §5.3 names, and it shouts inside a tab that simply has nothing in it
 * yet.
 */
@Composable
fun SwetEmptyState(
    title: String,
    modifier: Modifier = Modifier,
    body: String? = null,
    actionText: String? = null,
    onAction: (() -> Unit)? = null,
    illustration: (@Composable () -> Unit)? = null,
) {
    val colors = SwetgramThemeTokens.colors

    Column(
        modifier = modifier
            .fillMaxSize()
            // Wider than the screen margin on purpose: it keeps the two lines short enough to
            // scan and stops the action button from spanning the whole width.
            .padding(horizontal = Space.six, vertical = Space.four),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        if (illustration != null) {
            // Cleared HERE rather than in each caller. SDS §10 illustrations are decoration: they
            // support the sentence under them and never carry information of their own, so a
            // screen reader must walk straight past them to the title. Leaving that to the caller
            // is a defect that is invisible on screen -- everyone who can see the picture reads
            // the state correctly, and the one audience that cannot stops on a node with nothing
            // to say. A component that reserves the slot should guarantee the behaviour of it.
            Box(Modifier.clearAndSetSemantics { }) { illustration() }
            Spacer(Modifier.height(Space.three))
        }

        Text(
            text = title,
            style = MaterialTheme.typography.headlineSmall,
            color = colors.textPrimary,
            textAlign = TextAlign.Center,
        )

        if (body != null) {
            Spacer(Modifier.height(Space.one))
            Text(
                text = body,
                style = MaterialTheme.typography.bodyMedium,
                color = colors.textSecondary,
                textAlign = TextAlign.Center,
            )
        }

        if (actionText != null && onAction != null) {
            Spacer(Modifier.height(Space.four))
            SwetPrimaryButton(text = actionText, onClick = onAction)
        }
    }
}

// -------------------------------------------------------------------------------------------
// Error banner
// -------------------------------------------------------------------------------------------

/**
 * A failure reported ABOVE the content, never in place of it.
 *
 * This is the rule the app already holds -- both `ChatListScreen` and `ChatScreen` draw their
 * error over the list rather than swapping the list out -- and it is the whole reason this is a
 * banner and not a screen. A refresh that fails must not blank the conversations the user
 * already has; a cache that is still on screen is more useful than an apology.
 *
 * Red is used as the semantic failure signal of §4.6, applied as a 12% tint rather than a fill,
 * which keeps §4.8's "grayscale for most UI" intact. §18.2 governs [message]: say what happened
 * and what to do next, never the raw error.
 */
@Composable
fun SwetErrorBanner(
    message: String,
    modifier: Modifier = Modifier,
    onRetry: (() -> Unit)? = null,
    retryText: String = DEFAULT_RETRY_LABEL,
    onDismiss: (() -> Unit)? = null,
    dismissContentDescription: String = DEFAULT_DISMISS_LABEL,
) {
    val colors = SwetgramThemeTokens.colors

    Column(modifier.fillMaxWidth()) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(colors.danger.copy(alpha = 0.12f))
                .padding(start = Space.two, end = Space.half, top = Space.one, bottom = Space.one),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            // Decorative on purpose -- the message beside it says what went wrong, so a description
            // here would only make a screen reader announce "Error" before reading the same thing
            // in words.
            SwetIcon(
                icon = SwetIcons.AlertCircle,
                tint = colors.danger,
                contentDescription = null,
                size = Sizes.iconMedium,
            )

            Spacer(Modifier.width(Space.one))

            Text(
                text = message,
                style = MaterialTheme.typography.bodySmall,
                color = colors.textPrimary,
                modifier = Modifier
                    .weight(1f)
                    // The live region sits on the MESSAGE, not on the banner: it is announced
                    // when it appears without stealing focus, and putting it on a container
                    // that also holds Retry and Dismiss would fold those two buttons into the
                    // same node. A banner nobody hears is a banner that is not there for half
                    // the audience.
                    .semantics { liveRegion = LiveRegionMode.Polite },
            )

            if (onRetry != null) {
                SwetTextButton(text = retryText, onClick = onRetry)
            }

            if (onDismiss != null) {
                SwetIconButton(
                    contentDescription = dismissContentDescription,
                    onClick = onDismiss,
                ) { tint ->
                    SwetIcon(
                        icon = SwetIcons.Close,
                        tint = tint,
                        contentDescription = null,
                        size = Sizes.iconSmall,
                    )
                }
            }
        }

        HorizontalDivider(
            thickness = Sizes.borderHairline,
            color = colors.danger.copy(alpha = 0.32f),
        )
    }
}

// -------------------------------------------------------------------------------------------
// Loading
// -------------------------------------------------------------------------------------------

/**
 * A centred spinner, for the case where there is genuinely nothing to draw yet.
 *
 * SDS §2.1 says to avoid loading screens unless necessary, so this should be the fallback:
 * where a list is about to arrive, [SwetListRowSkeleton] keeps the layout still instead of
 * flashing a spinner and then reflowing.
 */
@Composable
fun SwetLoading(
    modifier: Modifier = Modifier,
    label: String? = null,
    contentDescription: String = DEFAULT_LOADING_LABEL,
) {
    val colors = SwetgramThemeTokens.colors
    val description = contentDescription

    Column(
        modifier = modifier
            .fillMaxSize()
            .padding(Space.three),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        CircularProgressIndicator(
            // §4.8: one accent. The default would pull `primary` from the derived scheme, which
            // is the same blue -- naming it here keeps it true if that mapping ever moves.
            color = colors.accent,
            modifier = Modifier
                .size(Sizes.iconLarge)
                .then(
                    // When there is a visible label the spinner stays silent, otherwise the
                    // state is announced twice.
                    if (label == null) {
                        Modifier.semantics { this.contentDescription = description }
                    } else {
                        Modifier.clearAndSetSemantics { }
                    },
                ),
        )

        if (label != null) {
            Spacer(Modifier.height(Space.two))
            Text(
                text = label,
                style = MaterialTheme.typography.bodySmall,
                color = colors.textSecondary,
                textAlign = TextAlign.Center,
            )
        }
    }
}

// -------------------------------------------------------------------------------------------
// Skeletons
// -------------------------------------------------------------------------------------------

/**
 * A placeholder block in the card surface, breathing while it waits.
 *
 * The block's first job is still to be a RESERVED SPACE: it holds the layout still so the list
 * does not jump when the real rows arrive, which is what §2.1 is actually asking for.
 *
 * It used to be deliberately static, on the grounds that §19.4 rules out "Decorative loops". That
 * is now read more narrowly, and the note is left here because the reasoning matters more than
 * the outcome. A skeleton is not decoration -- §25 lists Loading among the states every component
 * must define and §29 will not pass a screen without one, and a grid of motionless grey bars is
 * indistinguishable from a screen that finished loading and rendered wrong. The pulse is the only
 * thing saying the app is still working, and it ends when the content arrives, so it is bounded
 * by the request rather than looping for its own sake.
 *
 * A travelling shimmer WOULD be the decorative version, and is still refused: it draws the eye
 * across the screen and says nothing the opacity does not.
 *
 * See [swetSkeletonPulse] for the timing and for why reduced motion gets the static block back.
 *
 * The caller sizes it. Skeletons carry no semantics -- the container announces the load once,
 * rather than a screen reader walking eight identical grey bars.
 */
@Composable
fun SwetSkeleton(
    modifier: Modifier = Modifier,
    shape: Shape = RoundedCornerShape(Radius.xs),
) {
    Box(
        modifier = modifier
            .clearAndSetSemantics { }
            .swetSkeletonPulse()
            .background(SwetgramThemeTokens.colors.card, shape),
    )
}

/**
 * A conversation row's worth of placeholder, laid out on the same 72dp grid as [SwetListRow] so
 * the real rows land exactly where the skeletons were.
 *
 * The bars are deliberately unequal: two identical widths read as a rendering bug, whereas a
 * short title over a long preview reads as a row that has not loaded.
 *
 * [avatarSize] EXISTS BECAUSE THE PROMISE IN THE FIRST PARAGRAPH IS FALSIFIABLE. The disc has to be
 * the disc the real row draws, or the placeholder is not standing in the right place: the text
 * column starts at `Space.two + avatarSize + Space.two`, so a skeleton 8dp narrower than its row
 * shifts every title and preview sideways the moment the data lands. The default is
 * [Sizes.avatarMedium], which is what every list except the redrawn inbox still draws; the chat list
 * passes [Sizes.avatarChatRow]. Same shape, and same reason, as `SwetSearchBar`'s `cornerRadius`.
 */
@Composable
fun SwetListRowSkeleton(
    modifier: Modifier = Modifier,
    avatarSize: Dp = Sizes.avatarMedium,
) {
    Row(
        modifier = modifier
            .fillMaxWidth()
            .height(TouchTarget.chatRowHeight)
            .padding(horizontal = Space.two, vertical = Space.one),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SwetSkeleton(
            modifier = Modifier.size(avatarSize),
            shape = CircleShape,
        )

        Spacer(Modifier.width(Space.two))

        Column(Modifier.weight(1f)) {
            SwetSkeleton(
                Modifier
                    .fillMaxWidth(0.42f)
                    .height(Space.two),
            )
            Spacer(Modifier.height(Space.one))
            SwetSkeleton(
                Modifier
                    .fillMaxWidth(0.78f)
                    .height(Space.one + Space.half),
            )
        }

        Spacer(Modifier.width(Space.two))

        SwetSkeleton(
            Modifier
                .width(Space.four)
                .height(Space.one + Space.half),
        )
    }
}
